import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { AsyncBoundary, Notice } from '@kollez/design-system';
import {
  useGoverningEntities,
  type GoverningEntityType, type GoverningEntityStatus,
} from '@/lib/queries';

/**
 * /group/governing-entities (Page 74/N) — the legal parents behind
 * institutions: Trusts, Societies, Section-8 companies, Government
 * bodies, autonomous bodies. Axis-4 collection; NIRF/NAAC + AICTE
 * disclosures cite the entity, not the college.
 */

const TYPE_LABEL: Record<GoverningEntityType, string> = {
  Trust: 'Trust',
  Society: 'Society',
  Section_8_Company: 'Section-8 company',
  Government: 'Government',
  Autonomous_Body: 'Autonomous body',
  Other: 'Other',
};

const STATUS_LABEL: Record<GoverningEntityStatus, string> = {
  Active: 'Active', Inactive: 'Inactive', Suspended: 'Suspended', Dissolved: 'Dissolved',
};

function statusTone(s: GoverningEntityStatus): string {
  if (s === 'Active') return 'badge-success';
  if (s === 'Suspended') return 'badge-warning';
  if (s === 'Dissolved') return 'badge-danger';
  return '';
}

export function GroupGoverningEntitiesPage() {
  const nav = useNavigate();
  const [entityType, setEntityType] = useState<GoverningEntityType | ''>('');
  const [status, setStatus] = useState<GoverningEntityStatus | ''>('Active');
  const [q, setQ] = useState('');

  const list = useGoverningEntities({ entityType, status, q, sortBy: 'entityName', sortDir: 'asc', limit: 100 });
  const rows = list.data?.rows ?? [];
  const byType = list.data?.summary?.byType ?? {};

  return (
    <div className="stack">
      <div className="page-header">
        <div className="ph-text">
          <p className="eyebrow">Compliance · Axis-4</p>
          <h1 className="h-page">Governing entities</h1>
          <p className="subhead">
            The legal parents behind your institutions. NIRF / NAAC / AICTE disclosures
            cite the entity's registration number; keeping this current is a compliance
            requirement.
          </p>
        </div>
      </div>

      <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
        <div className="card kpi"><span className="kpi-label">Trusts</span>
          <span className="kpi-value">{byType.Trust ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Societies</span>
          <span className="kpi-value">{byType.Society ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Section-8</span>
          <span className="kpi-value">{byType.Section_8_Company ?? 0}</span></div>
        <div className="card kpi"><span className="kpi-label">Government</span>
          <span className="kpi-value">{byType.Government ?? 0}</span></div>
      </div>

      <div className="card">
        <div className="filterbar">
          <input className="input" placeholder="Search entity name or registration number" value={q} onChange={(e) => setQ(e.target.value)} />
          <select className="select" value={entityType} onChange={(e) => setEntityType(e.target.value as GoverningEntityType | '')}>
            <option value="">Any type</option>
            {(Object.entries(TYPE_LABEL) as Array<[GoverningEntityType, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select className="select" value={status} onChange={(e) => setStatus(e.target.value as GoverningEntityStatus | '')}>
            <option value="">Any status</option>
            {(Object.entries(STATUS_LABEL) as Array<[GoverningEntityStatus, string]>).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <span className="fb-count">{list.data?.meta?.total ?? rows.length}</span>
        </div>

        <AsyncBoundary
          kind="table"
          inCard
          loading={list.isLoading}
          error={list.error}
          empty={!list.isLoading && rows.length === 0}
          emptyTitle="No entities in scope"
          emptyHint="Add the trust/society that owns your colleges to unlock NIRF/NAAC autofill."
          onRetry={() => void list.refetch()}
        >
          <div className="tbl-wrap">
            <table className="tbl">
              <thead>
                <tr>
                  <th>Entity</th>
                  <th>Type</th>
                  <th>Registration</th>
                  <th>Location</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={r._id}
                    onClick={() => nav(`/group/governing-entities/${r._id}`)}
                    tabIndex={0}
                    style={{ cursor: 'pointer' }}
                    onKeyDown={(e) => { if (e.key === 'Enter') nav(`/group/governing-entities/${r._id}`); }}
                  >
                    <td className="cell-title">{r.entityName}</td>
                    <td className="cell-sub">{TYPE_LABEL[r.entityType]}</td>
                    <td className="cell-sub">
                      {r.registrationNumber ?? '—'}
                      {r.panNumber && <div className="cell-sub">PAN {r.panNumber}</div>}
                    </td>
                    <td className="cell-sub">{[r.city, r.state].filter(Boolean).join(', ') || '—'}</td>
                    <td><span className={`badge ${statusTone(r.status)}`}>{STATUS_LABEL[r.status]}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </AsyncBoundary>
      </div>

      <Notice tone="info">
        <b>Registration number is the source of truth.</b> If it changes (transfer, new
        MoA), record a new entity and mark the old one dissolved — never edit the number
        in place.
      </Notice>
    </div>
  );
}
